import { Editor, Transforms, Element as SlateElement } from "slate";

const alignment = ["alignLeft", "alignRight", "alignCenter"];
const list_types = ["orderedList", "unorderedList"];

export const sizeMap = {
  small: "12px",
  normal: "16px",
  medium: "28px",
  huge: "40px",
};
export const fontFamilyMap = {
  sans: "Helvetica,Arial, sans serif",
  serif: "Georgia, Times New Roaman,serif",
  monospace: "Monaco, Courier New,monospace",
};
export const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(editor, format);
  const isList = list_types.includes(format);
  const isIndent = alignment.includes(format);
  const isAligned = alignment.some((alignmentType) =>
    isBlockActive(editor, alignmentType)
  );

  /*If the node is already aligned and change in indent is called we should unwrap it first and split the node to prevent
    messy, nested DOM structure and bugs due to that.*/
  if (isAligned && isIndent) {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        alignment.includes(
          !Editor.isEditor(n) && SlateElement.isElement(n) && n.type
        ),
      split: true,
    });
  }

  /* Wraping the nodes for alignment, to allow it to co-exist with other block level operations*/
  if (isIndent) {
    Transforms.wrapNodes(editor, {
      type: format,
      children: [],
    });
    return;
  }
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      list_types.includes(
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type
      ),
    split: true,
  });

  Transforms.setNodes(editor, {
    type: isActive ? "paragraph" : isList ? "listItem" : format,
  });

  if (isList && !isActive) {
    Transforms.wrapNodes(editor, {
      type: format,
      children: [],
    });
  }
};
export const addMarkData = (editor, data) => {
  Editor.addMark(editor, data.format, data.value);
};
export const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};
export const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);

  return marks ? marks[format] === true : false;
};

export const isBlockActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
  });

  return !!match;
};

export const activeMark = (editor, format) => {
  const defaultMarkData = {
    color: "black",
    bgColor: "black",
    fontSize: "normal",
    fontFamily: "sans",
  };
  const marks = Editor.marks(editor);
  const defaultValue = defaultMarkData[format];
  return marks?.[format] ?? defaultValue;
};

export const getMarked = (leaf, children) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.strikethrough) {
    children = (
      <span style={{ textDecoration: "line-through" }}>{children}</span>
    );
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  if (leaf.superscript) {
    children = <sup>{children}</sup>;
  }
  if (leaf.subscript) {
    children = <sub>{children}</sub>;
  }

  if (leaf.fontSize) {
    const size = sizeMap[leaf.fontSize];
    children = <span style={{ fontSize: size }}>{children}</span>;
  }
  if (leaf.fontFamily) {
    const family = fontFamilyMap[leaf.fontFamily];
    children = <span style={{ fontFamily: family }}>{children}</span>;
  }
  return children;
};

export const getBlock = (props) => {
  const { element, children, caller } = props;
  const attributes = props.attributes ?? {};

  const isTextModified = (caller && caller === "handleEditorChange") ? true : false;
  let wrapInDIV = false;
  
  switch (element.type) {
    case "bold":
      {
        if(isTextModified){
          return <>{children}</>
        } else {
          wrapInDIV = true;
          break;
        }
      }
    case "paragraph":
      { 
        if(isTextModified){
          if(element.children[0]?.type == "paragraph"){
            return <>{children}</>;
          } else {
            return (
              <p {...element.attr} {...attributes}>
                {children}
              </p>
            );
          }
        } else {
          wrapInDIV = true;
          break;
        }
      }
    case "headingOne":
      return (
        <h1 {...attributes} {...element.attr}>
          {children}
        </h1>
      );
    case "headingTwo":
      return (
        <h2 {...attributes} {...element.attr}>
          {children}
        </h2>
      );
    case "headingThree":
      return (
        <h3 {...attributes} {...element.attr}>
          {children}
        </h3>
      );
    case "blockquote":
      return (
        <blockquote
          {...attributes}
          className="instruction_textEditor_blockquote"
          {...element.attr}
        >
          {children}
        </blockquote>
      );
    case "alignLeft":
      return (
        <div
          style={{ listStylePosition: "inside" }}
          {...attributes}
          {...element.attr}
        >
          {children}
        </div>
      );
    case "alignCenter":
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            listStylePosition: "inside",
            flexDirection: "column",
          }}
          {...attributes}
          {...element.attr}
        >
          {children}
        </div>
      );
    case "alignRight":
      return (
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            listStylePosition: "inside",
            flexDirection: "column",
          }}
          {...attributes}
          {...element.attr}
        >
          {children}
        </div>
      );
    case "listItem":
      return (
        <li {...attributes} {...element.attr}>
          {children}
        </li>
      );
    case "orderedList":
      return (
        <ol type="1" {...attributes}>
          {children}
        </ol>
      );
    case "unorderedList":
      return <ul {...attributes}>{children}</ul>;

    default: 
      wrapInDIV = true;
  }

  if(wrapInDIV){
    return (
      <div {...element.attr} {...attributes}>
        {children}
      </div>
    );
  }
};
